import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedClient, queryKeys } from '@/api/queries'
import { POLL_ANALYSIS, POLL_NORMAL } from '@/api/polling'
import type { components } from '@/api/schema'
import { useThreadMailbox } from '@/features/communications/mailbox'

type CustomerThreadDetail = components['schemas']['CustomerCommunicationThreadDetailRead']
type CustomerMessage = CustomerThreadDetail['messages'][number]
type CustomerAnalysis = CustomerThreadDetail['analysis'][number]

/** Customer threads are cached one per detail query (not as a list); the detail is the only shape patched. */
function isCustomerThread(value: unknown): value is CustomerThreadDetail {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { messages?: unknown }).messages) &&
    Array.isArray((value as { analysis?: unknown }).analysis)
  )
}

/** Optimistically change one message on whichever customer thread caches it. */
async function patchCustomerMessage(
  queryClient: ReturnType<typeof useQueryClient>,
  messageUuid: string,
  patch: Partial<CustomerMessage>,
) {
  await queryClient.cancelQueries({ queryKey: queryKeys.customerComms.all })
  queryClient.setQueriesData({ queryKey: queryKeys.customerComms.all }, (old: unknown) =>
    isCustomerThread(old)
      ? { ...old, messages: old.messages.map((m) => (m.uuid === messageUuid ? { ...m, ...patch } : m)) }
      : old,
  )
}

function invalidateCustomerThreads(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.all })
}

export function useProjectCommunications(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.communications.byProject(projectUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/communications/by-project/{project_uuid}', {
        params: { path: { project_uuid: projectUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!projectUuid,
    refetchInterval: POLL_NORMAL,
  })
}

export function usePOCommunications(poUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.communications.byPO(poUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/communications/by-purchase-order/{po_uuid}', {
        params: { path: { po_uuid: poUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!poUuid,
    // While the AI works on a message, check back often so its result lands without a reload.
    refetchInterval: (query) =>
      query.state.data?.some((thread) =>
        thread.analysis.some((a) => a.state === 'queued' || a.state === 'running'),
      )
        ? POLL_ANALYSIS
        : POLL_NORMAL,
  })
}

export function useMessageAttachments(messageUuid: string | undefined, enabled = true) {
  const getClient = useAuthenticatedClient()
  const mailbox = useThreadMailbox()
  return useQuery({
    queryKey:
      mailbox === 'customer'
        ? (['customer-comms', 'attachments', messageUuid] as const)
        : (['communications', 'attachments', messageUuid] as const),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } =
        mailbox === 'customer'
          ? await client.GET('/dashboard/customer-communications/messages/{message_uuid}/attachments', {
              params: { path: { message_uuid: messageUuid! } },
            })
          : await client.GET(
              '/dashboard/communications/messages/{message_uuid}/attachments',
              { params: { path: { message_uuid: messageUuid! } } },
            )
      if (error) throw error
      return data
    },
    enabled: !!messageUuid && enabled,
    staleTime: 60_000,
  })
}

export function useComposeMessage(threadUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { subject?: string; body_text: string; to_emails?: string[]; cc_emails?: string[] }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/communications/threads/{thread_uuid}/messages', {
        params: { path: { thread_uuid: threadUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useSendReply(threadUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const mailbox = useThreadMailbox()
  return useMutation({
    mutationFn: async (body: {
      body_text: string
      to_emails?: string[]
      cc_emails?: string[]
      attach_po_pdf?: boolean
      attach_quote_pdf?: boolean
      attach_shipment_docs?: Array<{ shipment_id: number; doc_type: 'bol' | 'label' }>
      attachments?: Array<{ filename: string; content_type: string; content_base64: string }>
    }) => {
      const client = await getClient()
      if (mailbox === 'customer') {
        // Customer replies carry no PO, quote or shipment documents.
        const { data, error } = await client.POST(
          '/dashboard/customer-communications/threads/{thread_uuid}/reply',
          {
            params: { path: { thread_uuid: threadUuid } },
            body: {
              body_text: body.body_text,
              to_emails: body.to_emails,
              cc_emails: body.cc_emails,
              attachments: body.attachments ?? [],
            },
          },
        )
        if (error) throw error
        return data
      }
      const { data, error } = await client.POST('/dashboard/communications/threads/{thread_uuid}/reply', {
        params: { path: { thread_uuid: threadUuid } },
        body: {
          body_text: body.body_text,
          to_emails: body.to_emails,
          cc_emails: body.cc_emails,
          attach_po_pdf: body.attach_po_pdf ?? false,
          attach_quote_pdf: body.attach_quote_pdf ?? false,
          attach_shipment_docs: body.attach_shipment_docs ?? [],
          attachments: body.attachments ?? [],
        },
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      if (mailbox === 'customer') {
        invalidateCustomerThreads(queryClient)
        return
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useForwardMessage(messageUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { to_emails: string[]; cc_emails?: string[]; comment?: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/communications/messages/{message_uuid}/forward',
        {
          params: { path: { message_uuid: messageUuid } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useRecordCommunication(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { body_text: string; source?: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/communications/by-purchase-order/{po_uuid}/record',
        {
          params: { path: { po_uuid: poUuid } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useUpdateMessage() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const mailbox = useThreadMailbox()
  return useMutation({
    mutationFn: async ({
      messageUuid,
      body,
    }: {
      messageUuid: string
      // To and CC change only before the draft reaches Outlook (USA-1663).
      body: { subject?: string; body_text?: string; body_html?: string; to_emails?: string[]; cc_emails?: string[] }
    }) => {
      const client = await getClient()
      if (mailbox === 'customer') {
        const { data, error } = await client.PATCH(
          '/dashboard/customer-communications/messages/{message_uuid}',
          {
            params: { path: { message_uuid: messageUuid } },
            body: {
              subject: body.subject,
              body_text: body.body_text,
              ...(body.to_emails ? { to_emails: body.to_emails } : {}),
              ...(body.cc_emails ? { cc_emails: body.cc_emails } : {}),
            },
          },
        )
        if (error) throw error
        return data
      }
      const { data, error } = await client.PATCH('/dashboard/communications/messages/{message_uuid}', {
        params: { path: { message_uuid: messageUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    // Customer edits show at once; supplier edits keep their original wait-for-refetch behavior.
    onMutate:
      mailbox === 'customer'
        ? async ({ messageUuid, body }) => {
            if (body.body_text !== undefined) {
              await patchCustomerMessage(queryClient, messageUuid, { body_text: body.body_text })
            }
          }
        : undefined,
    onSuccess: () => {
      if (mailbox === 'customer') {
        invalidateCustomerThreads(queryClient)
        return
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
    },
    onError: mailbox === 'customer' ? () => invalidateCustomerThreads(queryClient) : undefined,
  })
}

type FollowUpUpdate = { clear?: boolean; follow_up_at?: string | null; max_follow_ups?: number }

export function useUpdateThreadFollowUp() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const mailbox = useThreadMailbox()
  return useMutation({
    mutationFn: async ({ threadUuid, body }: { threadUuid: string; body: FollowUpUpdate }) => {
      const client = await getClient()
      if (mailbox === 'customer') {
        const { data, error } = await client.PATCH(
          '/dashboard/customer-communications/threads/{thread_uuid}/follow-up',
          {
            params: { path: { thread_uuid: threadUuid } },
            body: { follow_up_at: body.follow_up_at, clear: body.clear ?? false },
          },
        )
        if (error) throw error
        return data
      }
      const { data, error } = await client.PATCH(
        '/dashboard/communications/threads/{thread_uuid}/follow-up',
        {
          params: { path: { thread_uuid: threadUuid } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      if (mailbox === 'customer') {
        invalidateCustomerThreads(queryClient)
        return
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useApproveMessage() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const mailbox = useThreadMailbox()
  return useMutation({
    mutationFn: async (messageUuid: string) => {
      const client = await getClient()
      if (mailbox === 'customer') {
        const { data, error } = await client.POST(
          '/dashboard/customer-communications/messages/{message_uuid}/approve',
          { params: { path: { message_uuid: messageUuid } } },
        )
        if (error) throw error
        return data
      }
      const { data, error } = await client.POST('/dashboard/communications/messages/{message_uuid}/approve', {
        params: { path: { message_uuid: messageUuid } },
      })
      if (error) throw error
      return data
    },
    onMutate: async (messageUuid) => {
      if (mailbox === 'customer') {
        await patchCustomerMessage(queryClient, messageUuid, { status: 'sending' })
        return
      }
      await queryClient.cancelQueries({ queryKey: queryKeys.communications.all })
      // Optimistically update all communication caches that may contain this message
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateMessage = (old: any) => {
        if (!old) return old
        if (Array.isArray(old)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return old.map((thread: any) => ({
            ...thread,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            messages: thread.messages?.map((m: any) =>
              m.uuid === messageUuid ? { ...m, status: 'sending' } : m,
            ),
          }))
        }
        return old
      }
      queryClient.setQueriesData({ queryKey: queryKeys.communications.all }, updateMessage)
    },
    onSettled: () => {
      if (mailbox === 'customer') {
        invalidateCustomerThreads(queryClient)
        return
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useRejectMessage() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const mailbox = useThreadMailbox()
  return useMutation({
    mutationFn: async (messageUuid: string) => {
      const client = await getClient()
      if (mailbox === 'customer') {
        const { data, error } = await client.POST(
          '/dashboard/customer-communications/messages/{message_uuid}/reject',
          { params: { path: { message_uuid: messageUuid } } },
        )
        if (error) throw error
        return data
      }
      const { data, error } = await client.POST('/dashboard/communications/messages/{message_uuid}/reject', {
        params: { path: { message_uuid: messageUuid } },
      })
      if (error) throw error
      return data
    },
    onMutate: async (messageUuid) => {
      if (mailbox === 'customer') {
        await patchCustomerMessage(queryClient, messageUuid, { status: 'draft' })
        return
      }
      await queryClient.cancelQueries({ queryKey: queryKeys.communications.all })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateMessage = (old: any) => {
        if (!old) return old
        if (Array.isArray(old)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return old.map((thread: any) => ({
            ...thread,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            messages: thread.messages?.map((m: any) =>
              m.uuid === messageUuid ? { ...m, status: 'draft' } : m,
            ),
          }))
        }
        return old
      }
      queryClient.setQueriesData({ queryKey: queryKeys.communications.all }, updateMessage)
    },
    onSettled: () => {
      if (mailbox === 'customer') {
        invalidateCustomerThreads(queryClient)
        return
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
    },
  })
}

export function useSendMessage() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const mailbox = useThreadMailbox()
  return useMutation({
    mutationFn: async (messageUuid: string) => {
      const client = await getClient()
      if (mailbox === 'customer') {
        const { data, error } = await client.POST(
          '/dashboard/customer-communications/messages/{message_uuid}/send',
          { params: { path: { message_uuid: messageUuid } } },
        )
        if (error) throw error
        return data
      }
      const { data, error } = await client.POST('/dashboard/communications/messages/{message_uuid}/send', {
        params: { path: { message_uuid: messageUuid } },
      })
      if (error) throw error
      return data
    },
    onMutate:
      mailbox === 'customer'
        ? (messageUuid) => patchCustomerMessage(queryClient, messageUuid, { status: 'sending' })
        : undefined,
    onError: mailbox === 'customer' ? () => invalidateCustomerThreads(queryClient) : undefined,
    onSuccess: () => {
      if (mailbox === 'customer') {
        invalidateCustomerThreads(queryClient)
        return
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.apInvoices.all })
    },
  })
}

export function useRetryMessage() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const mailbox = useThreadMailbox()
  return useMutation({
    mutationFn: async (messageUuid: string) => {
      const client = await getClient()
      if (mailbox === 'customer') {
        const { data, error } = await client.POST(
          '/dashboard/customer-communications/messages/{message_uuid}/retry',
          { params: { path: { message_uuid: messageUuid } } },
        )
        if (error) throw error
        return data
      }
      const { data, error } = await client.POST('/dashboard/communications/messages/{message_uuid}/retry', {
        params: { path: { message_uuid: messageUuid } },
      })
      if (error) throw error
      return data
    },
    onMutate: async (messageUuid) => {
      if (mailbox === 'customer') {
        await patchCustomerMessage(queryClient, messageUuid, { status: 'sending', error_message: null })
        return
      }
      await queryClient.cancelQueries({ queryKey: queryKeys.communications.all })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateMessage = (old: any) => {
        if (!old) return old
        if (Array.isArray(old)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return old.map((thread: any) => ({
            ...thread,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            messages: thread.messages?.map((m: any) =>
              m.uuid === messageUuid ? { ...m, status: 'sending', error_message: null } : m,
            ),
          }))
        }
        return old
      }
      queryClient.setQueriesData({ queryKey: queryKeys.communications.all }, updateMessage)
    },
    onSettled: () => {
      if (mailbox === 'customer') {
        invalidateCustomerThreads(queryClient)
        return
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useUpdateThreadStatus(threadUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (status: string) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/communications/threads/{thread_uuid}/status', {
        params: { path: { thread_uuid: threadUuid } },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: { status } as any,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
    },
  })
}

export function useDefaultPORecipients(
  poUuid: string,
  enabled: boolean = true,
  template: 'po_email' | 'spec_credit' | 'blank' = 'po_email',
) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...queryKeys.communications.byPO(poUuid), 'default-recipients', template],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/communications/by-purchase-order/{po_uuid}/default-recipients',
        { params: { path: { po_uuid: poUuid }, query: { template } } },
      )
      if (error) throw error
      return data
    },
    enabled: enabled && !!poUuid,
  })
}

type CreateThreadBody = {
  template: 'po_email' | 'spec_credit' | 'blank'
  subject?: string
  recipient_email?: string
  recipient_name?: string
  cc_emails?: string[]
  body_text?: string
  body_html?: string
  attach_po_pdf: boolean
  attach_quote_pdf: boolean
}

export function useCreatePOThread(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateThreadBody) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/communications/by-purchase-order/{po_uuid}/threads',
        {
          params: { path: { po_uuid: poUuid } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useUpdateThreadRecipients() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      threadUuid,
      recipient_email,
      recipient_name,
      cc_emails,
    }: {
      threadUuid: string
      recipient_email?: string
      recipient_name?: string | null
      cc_emails?: string[]
    }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/communications/threads/{thread_uuid}/recipients',
        {
          params: { path: { thread_uuid: threadUuid } },
          body: { recipient_email, recipient_name, cc_emails },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
    },
  })
}

export function useCloseThread() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const mailbox = useThreadMailbox()
  return useMutation({
    mutationFn: async (threadUuid: string) => {
      const client = await getClient()
      if (mailbox === 'customer') {
        const { data, error } = await client.POST(
          '/dashboard/customer-communications/threads/{thread_uuid}/close',
          { params: { path: { thread_uuid: threadUuid } } },
        )
        if (error) throw error
        return data
      }
      const { data, error } = await client.POST(
        '/dashboard/communications/threads/{thread_uuid}/close',
        { params: { path: { thread_uuid: threadUuid } } },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      if (mailbox === 'customer') {
        invalidateCustomerThreads(queryClient)
        return
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
    },
  })
}

export function useReopenThread() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const mailbox = useThreadMailbox()
  return useMutation({
    mutationFn: async ({
      threadUuid,
      status = 'awaiting_reply',
    }: {
      threadUuid: string
      status?: 'awaiting_reply' | 'replied' | 'needs_attention'
    }) => {
      const client = await getClient()
      if (mailbox === 'customer') {
        const { data, error } = await client.POST(
          '/dashboard/customer-communications/threads/{thread_uuid}/reopen',
          {
            params: { path: { thread_uuid: threadUuid } },
            body: { status },
          },
        )
        if (error) throw error
        return data
      }
      const { data, error } = await client.POST(
        '/dashboard/communications/threads/{thread_uuid}/reopen',
        {
          params: { path: { thread_uuid: threadUuid } },
          body: { status },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      if (mailbox === 'customer') {
        invalidateCustomerThreads(queryClient)
        return
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
    },
  })
}

export function useSendPOEmails() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (purchaseOrderUuids: string[]) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/communications/send-po-emails', {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: { purchase_order_uuids: purchaseOrderUuids } as any,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardSummary.all })
    },
  })
}

/** An AP invoice's conversations, in the shape the PO conversation view reads (USA-1418). */
export function useApInvoiceCommunications(invoiceUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...queryKeys.communications.all, 'ap-invoice', invoiceUuid],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/communications/by-ap-invoice/{invoice_uuid}', {
        params: { path: { invoice_uuid: invoiceUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!invoiceUuid,
    refetchInterval: POLL_NORMAL,
  })
}

export function usePOInvoiceCommunications(poUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...queryKeys.communications.byPO(poUuid), 'invoice-threads'],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/communications/by-purchase-order/{po_uuid}/invoice-threads',
        { params: { path: { po_uuid: poUuid } } },
      )
      if (error) throw error
      return data
    },
    enabled: !!poUuid,
    refetchInterval: POLL_NORMAL,
  })
}

/** Run the AI on a PO-thread (or customer-thread) email again now, after it could not finish. */
export function useRetryAnalysis() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const mailbox = useThreadMailbox()
  return useMutation({
    mutationFn: async (messageId: number) => {
      const client = await getClient()
      if (mailbox === 'customer') {
        const { data, error } = await client.POST('/dashboard/communications/analysis/retry', {
          body: { family: 'customer', message_id: messageId },
        })
        if (error) throw error
        return data
      }
      const { data, error } = await client.POST('/dashboard/communications/analysis/retry', {
        body: { family: 'manufacturer', message_id: messageId },
      })
      if (error) throw error
      return data
    },
    onMutate: async (messageId) => {
      if (mailbox === 'customer') {
        await queryClient.cancelQueries({ queryKey: queryKeys.customerComms.all })
        const markQueued = (a: CustomerAnalysis): CustomerAnalysis =>
          a.message_id === messageId ? { ...a, state: 'queued', retry_at: null } : a
        queryClient.setQueriesData({ queryKey: queryKeys.customerComms.all }, (old: unknown) =>
          isCustomerThread(old) ? { ...old, analysis: old.analysis.map(markQueued) } : old,
        )
        return
      }
      await queryClient.cancelQueries({ queryKey: queryKeys.communications.all })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const markQueued = (old: any) =>
        Array.isArray(old)
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            old.map((thread: any) => ({
              ...thread,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              analysis: thread.analysis?.map((a: any) =>
                a.message_id === messageId ? { ...a, state: 'queued', retry_at: null } : a,
              ),
            }))
          : old
      queryClient.setQueriesData({ queryKey: queryKeys.communications.all }, markQueued)
    },
    onSettled: () => {
      if (mailbox === 'customer') {
        invalidateCustomerThreads(queryClient)
        return
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}
